import type { Tag } from '@epasu/core';
import type { CampaignMemberState, FollowupState } from '@epasu/core/constants';

/**
 * Der abgeleitete Zustand eines Leads.
 *
 * Keine gespeicherte Spalte: hergeleitet aus der Kampagnen-Mitgliedschaft und
 * der Nachfass-Uhr der zugehörigen Erstansprache — beides existiert seit
 * Task 7 wirklich, deshalb lässt sich der frühere Zweizustand (nur „noch
 * nicht angeschrieben“ vs. „qualifiziert“, siehe Task 6) hier zum echten
 * Trichter ausbauen.
 *
 * Reihenfolge = Priorität: eine offene Kampagnen-Mitgliedschaft schlägt den
 * „noch nicht angeschrieben“-Befund (der träfe sonst auch zu, siehe
 * `ableiteZustand`), und die Uhr schlägt beides.
 */
export type Zustand =
  | 'noch_nicht_angeschrieben'
  | 'in_kampagne_offen'
  | 'erstansprache_versendet'
  | 'follow_up_faellig'
  | 'antwort_offen'
  | 'kalt'
  | 'qualifiziert';

export const ZUSTAND_LABEL: Record<Zustand, string> = {
  noch_nicht_angeschrieben: 'Noch nicht angeschrieben',
  in_kampagne_offen: 'In Kampagne offen',
  erstansprache_versendet: 'Erstansprache versendet',
  follow_up_faellig: 'Follow-up fällig',
  antwort_offen: 'Antwort offen',
  kalt: 'Kalt',
  qualifiziert: 'Qualifiziert',
};

/** Alle Zustände, in der Reihenfolge, in der der Filter sie anbietet. */
export const ZUSTAND_REIHENFOLGE: Zustand[] = [
  'noch_nicht_angeschrieben',
  'in_kampagne_offen',
  'erstansprache_versendet',
  'follow_up_faellig',
  'antwort_offen',
  'kalt',
  'qualifiziert',
];

/** Kampagnen-Mitgliedschaft eines Leads, soweit für den Zustand relevant. */
export interface KampagnenMitgliedschaft {
  state: CampaignMemberState;
  followupState: FollowupState | null;
}

/**
 * Leitet den Zustand her.
 *
 * `gestoppt` fällt unter „kalt“: beides heißt „die Uhr ist fertig, es kommt
 * nichts mehr von selbst“ — ob das letzte Nachfassen ausblieb oder jemand von
 * Hand abgebrochen hat, ändert nichts an der nächsten Handlung.
 *
 * `eigeneUhr` ist der Uhrstand der Erstansprache selbst, unabhängig von einer
 * Kampagnen-Mitgliedschaft — nötig, weil `campaigns.members()` eine von Hand
 * verschickte Erstansprache **außerhalb** jeder Kampagne nie sieht
 * (`ApprovalsPage`s eigener Kommentar nennt diese Zeilen „ohne Kampagne“).
 * Ohne diesen Parameter fiel so ein Lead mit einer echten laufenden Uhr durch
 * den Switch und landete am Auffangzustand „qualifiziert“ — genau die
 * Zwei-Wahrheiten-Falle, vor der die Spec warnt.
 *
 * Reihenfolge = Priorität, und zwar strikt: die Uhr (Kampagne oder nicht)
 * schlägt eine offene Kampagnen-Mitgliedschaft, die wiederum den „noch nicht
 * angeschrieben“-Befund schlägt (der träfe sonst auch zu). Praktisch
 * überschneiden sich die ersten beiden nie: eine Mitgliedschaft trägt nur
 * dann `state === 'offen'`, wenn noch keine Nachricht verschickt wurde
 * (`sentMessageId` ist dann `null`), also auch kein `followupState`.
 *
 * Ohne Mitgliedschaft und ohne Uhr bleibt nur die alte Unterscheidung: nie
 * angeschrieben oder schon mal — Letzteres ist der Auffangzustand
 * „qualifiziert“ für alles, was keiner der spezifischeren Zustände trifft
 * (etwa eine ganz gewöhnliche Mail außerhalb einer Erstansprache).
 */
export function ableiteZustand(
  mitglied: KampagnenMitgliedschaft | undefined,
  nochNieAngeschrieben: boolean,
  eigeneUhr: FollowupState | null = null,
): Zustand {
  switch (mitglied?.followupState ?? eigeneUhr) {
    case 'offen':
      return 'erstansprache_versendet';
    case 'faellig':
      return 'follow_up_faellig';
    case 'beantwortet':
      return 'antwort_offen';
    case 'kalt':
    case 'gestoppt':
      return 'kalt';
    default:
      break;
  }

  if (mitglied?.state === 'offen') return 'in_kampagne_offen';

  return nochNieAngeschrieben ? 'noch_nicht_angeschrieben' : 'qualifiziert';
}

/** Tags, Listenzugehörigkeit und Zustand eines Leads, gebündelt geladen. */
export interface LeadZusatz {
  tags: Tag[];
  listIds: string[];
  zustand: Zustand;
}
